// Frontend entry point for the item-type matrix. The matrix itself lives in the
// cross-stack `@synapse/item-types` package (shared with the backend), so the
// fields this app renders and the fields the API accepts are the same
// declarations — nothing is mirrored, so the two sides cannot drift.
//
// This file used to be that mirror, with a comment explaining that the two
// tsconfigs could not share code. They can, and two other packages were already
// doing it. What stays here is the part that genuinely belongs to the client:
// unit display, which needs a translator, and the NMRA dosage-form mapping,
// which only the stock-in form uses.
//
// Mirrors how `src/lib/gestogram.ts` re-exports `@synapse/gestation`.

export * from '@synapse/item-types';

import { UNIT_OPTIONS } from '@synapse/item-types';

// Known preset units that have matching `unit.*` translations.
const KNOWN_UNITS = new Set<string>(Object.values(UNIT_OPTIONS).flat());

/**
 * Translate a unit string for display. Preset units (those in `UNIT_OPTIONS`)
 * resolve via `unit.<value>`; user-typed custom units pass through untouched
 * — there's no translation for them and returning the raw `unit.blister` key
 * would surface i18next's missing-key fallback to the user.
 */
export function translateUnit(t: (key: string) => string, unit: string): string {
  return KNOWN_UNITS.has(unit) ? t(`unit.${unit}`) : unit;
}

// NMRA `dosage_form` strings → our unit vocabulary. Permissive: anything
// unmapped returns null and the user picks. The dataset is noisy and we'd
// rather skip a guess than overwrite the field with something wrong.
//
// Client-side on purpose: it exists to pre-fill a form field from a reference
// lookup, and nothing on the server reads a dosage form.
const DOSAGE_FORM_TO_UNIT: Record<string, string> = {
  TABLETS: 'tablets',
  TABLET: 'tablets',
  'FILM-COATED TABLETS': 'tablets',
  'FILM COATED TABLETS': 'tablets',
  'COATED TABLETS': 'tablets',
  CAPSULES: 'capsules',
  CAPSULE: 'capsules',
  INJECTION: 'vials',
  'INJECTION SOLUTION': 'vials',
  VIAL: 'vials',
  VIALS: 'vials',
  SYRUP: 'ml',
  SUSPENSION: 'ml',
  SOLUTION: 'ml',
  DROPS: 'drops',
  'EYE DROPS': 'drops',
  SACHET: 'sachets',
  SACHETS: 'sachets',
  AMPOULE: 'ampoules',
  AMPOULES: 'ampoules',
};

export function dosageFormToUnit(dosageForm: string | null | undefined): string | null {
  if (!dosageForm) return null;
  return DOSAGE_FORM_TO_UNIT[dosageForm.trim().toUpperCase()] ?? null;
}
